import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { calculateDayPayroll } from '../../lib/api/salaryCalc'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import './ReportsPage.css'

const formatMoney = (val) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(val || 0)


function pad(n) {
  return String(n).padStart(2, '0')
}


function getPreviousDateString(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d - 1, 12, 0, 0))
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`
}

function formatHours(h) {
  const rounded = Math.round((Number(h) || 0) * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
}

function pluralize(n, one, two, five) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 19) return five
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return two
  return five
}

function formatDayTitle(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  return dt.toLocaleDateString('ru-RU', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    weekday: 'short'
  })
}

function getMonthLabel(year, month) {
  if (!year || !month) return 'Загрузка...'
  const dt = new Date(Date.UTC(year, month - 1, 1, 12, 0, 0))
  const monthName = dt.toLocaleDateString('ru-RU', { timeZone: 'UTC', month: 'long' })
  const cap = monthName.charAt(0).toUpperCase() + monthName.slice(1)
  return `${cap} ${year}`
}

/**
 * Синхронизирует текущее состояние страницы отчетов с URL браузера.
 * Формат: /reports?pvzId=<uuid>&month=YYYY-MM
 * Не добавляет from/to в публичный URL.
 */
export function syncReportsUrl(pvzId, year, month) {
  if (!pvzId || !year || !month) return
  const padNum = (n) => String(n).padStart(2, '0')
  const searchParams = new URLSearchParams()
  searchParams.set('pvzId', pvzId)
  searchParams.set('month', `${year}-${padNum(month)}`)
  const newUrl = `/reports?${searchParams.toString()}`
  if (typeof window !== 'undefined' && (window.location.pathname + window.location.search) !== newUrl) {
    window.history.replaceState(null, '', newUrl)
  }
}

/**
 * Обработчик переключения ПВЗ:
 * 1. Разрешает текущий видимый месяц из авторитетного UI-состояния (ref -> state -> URL -> pageParams).
 * 2. НЕМЕДЛЕННО синхронизирует URL браузера (/reports?pvzId=<newPvzId>&month=YYYY-MM) ДО старта бутстрапа.
 * 3. Вызывает setSelectedPvzId(newPvzId) для запуска серверного бутстрапа.
 */
export function executePvzSelection(newPvzId, { selectedPvzId, visibleMonthRef, currentYear, currentMonth, syncUrl = syncReportsUrl, setSelectedPvzId, pageParams }) {
  if (!newPvzId || newPvzId === selectedPvzId) return false

  let targetYear = visibleMonthRef?.current?.year || currentYear
  let targetMonth = visibleMonthRef?.current?.month || currentMonth

  if (!targetYear || !targetMonth) {
    const search = typeof window !== 'undefined' ? window.location.search : ''
    const urlParams = new URLSearchParams(search)
    const mParam = urlParams.get('month') || pageParams?.month
    if (mParam && /^\d{4}-\d{2}$/.test(mParam)) {
      const [pY, pM] = mParam.split('-').map(Number)
      targetYear = pY
      targetMonth = pM
    }
  }

  if (targetYear && targetMonth && syncUrl) {
    syncUrl(newPvzId, targetYear, targetMonth)
  }

  if (setSelectedPvzId) {
    setSelectedPvzId(newPvzId)
  }

  return true
}

/**
 * Формирует payload для перехода из раздела отчетов в детальный ежедневный отчет.
 * Передает контекст возврата returnTo ('reports') и returnParams (pvzId, month).
 */
export function buildDailyReportNavigationPayload(pvzId, dateStr, year, month) {
  const padNum = (n) => String(n).padStart(2, '0')
  const monthStr = (year && month) ? `${year}-${padNum(month)}` : (dateStr ? dateStr.slice(0, 7) : null)
  return {
    pvzId: pvzId || null,
    date: dateStr,
    returnTo: 'reports',
    returnParams: {
      pvzId: pvzId || null,
      month: monthStr
    }
  }
}

export default function ReportsPage({ onPageChange, pageParams }) {
  const { userPvzs = [], activePvz } = useAuth()

  // 1. Определение активного ПВЗ (browser URL имеет приоритет над pageParams)
  const [selectedPvzId, setSelectedPvzId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('pvzId') || pageParams?.pvzId || activePvz?.id || userPvzs[0]?.id || ''
  })

  useEffect(() => {
    if (!selectedPvzId && (activePvz?.id || userPvzs[0]?.id)) {
      const urlParams = new URLSearchParams(window.location.search)
      const initialPvz = urlParams.get('pvzId') || pageParams?.pvzId || activePvz?.id || userPvzs[0]?.id || ''
      if (initialPvz) {
        setSelectedPvzId(initialPvz)
      }
    }
  }, [activePvz?.id, userPvzs, selectedPvzId, pageParams?.pvzId])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [historyData, setHistoryData] = useState(null)
  const [retryTrigger, setRetryTrigger] = useState(0)

  // Текущий выбранный месяц (управляется сервером при начальной загрузке)
  const [currentYear, setCurrentYear] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(null)
  const visibleMonthRef = useRef({ year: null, month: null })
  const lastHandledExternalMonthRef = useRef(pageParams?.month || null)


  // Серверные даты
  const businessToday = historyData?.businessToday || ''
  const todayStr = businessToday
  const yesterdayStr = useMemo(() => getPreviousDateString(businessToday), [businessToday])

  const [todayYear, todayMonth] = useMemo(() => {
    if (!businessToday) return [null, null]
    return businessToday.split('-').map(Number)
  }, [businessToday])

  const isCurrentMonth = Boolean(todayYear && todayMonth && currentYear === todayYear && currentMonth === todayMonth)
  const isFutureOrCurrent = Boolean(
    !todayYear ||
    !todayMonth ||
    !currentYear ||
    !currentMonth ||
    currentYear > todayYear ||
    (currentYear === todayYear && currentMonth >= todayMonth)
  )

  // 2. Серверно-авторитетный начальный бутстрап (без N+1)
  useEffect(() => {
    let isCancelled = false

    if (!selectedPvzId) {
      setLoading(false)
      return
    }

    const bootstrap = async () => {
      setLoading(true)
      setError(null)

      try {
        // Первый вызов БЕЗ from и to: бэкенд вычисляет businessToday по pvz.timezone из БД
        // и сразу возвращает данные текущего бизнес-месяца
        const bootstrapRes = await api.getDailyReportsHistory(selectedPvzId)
        if (isCancelled) return

        const bToday = bootstrapRes.businessToday
        const [bY, bM] = bToday.split('-').map(Number)

        // Определение запрашиваемого месяца:
        // 1. При переключении ПВЗ сохраняем уже выбранный видимый месяц (visibleMonthRef).
        // 2. При начальной загрузке читаем browser URL (urlParams.get('month')).
        // 3. pageParams?.month используется только как начальный fallback, но не может перетирать URL/state.
        let requestedYear = null
        let requestedMonth = null

        if (visibleMonthRef.current.year && visibleMonthRef.current.month) {
          requestedYear = visibleMonthRef.current.year
          requestedMonth = visibleMonthRef.current.month
        } else {
          const urlParams = new URLSearchParams(window.location.search)
          const mParam = urlParams.get('month') || pageParams?.month
          if (mParam && /^\d{4}-\d{2}$/.test(mParam)) {
            const [pY, pM] = mParam.split('-').map(Number)
            requestedYear = pY
            requestedMonth = pM
          }
        }

        if (requestedYear && requestedMonth) {
          const isPastMonth = requestedYear < bY || (requestedYear === bY && requestedMonth < bM)

          if (isPastMonth) {
            // Запрошен архивный месяц: запрашиваем его явно
            const lastDay = new Date(Date.UTC(requestedYear, requestedMonth, 0)).getUTCDate()
            const from = `${requestedYear}-${pad(requestedMonth)}-01`
            const to = `${requestedYear}-${pad(requestedMonth)}-${pad(lastDay)}`
            const pastRes = await api.getDailyReportsHistory(selectedPvzId, from, to)
            if (isCancelled) return

            setCurrentYear(requestedYear)
            setCurrentMonth(requestedMonth)
            visibleMonthRef.current = { year: requestedYear, month: requestedMonth }
            lastHandledExternalMonthRef.current = `${requestedYear}-${pad(requestedMonth)}`
            setHistoryData(pastRes)
            syncReportsUrl(selectedPvzId, requestedYear, requestedMonth)
            return
          }
          // Если запрошенный месяц текущий или будущий — принудительно используем
          // текущий бизнес-месяц (bootstrapRes уже содержит его данные, 0 лишних запросов).
          // URL будет скорректирован ниже через syncReportsUrl к bY, bM (future month clamp).
        }

        setCurrentYear(bY)
        setCurrentMonth(bM)
        visibleMonthRef.current = { year: bY, month: bM }
        lastHandledExternalMonthRef.current = `${bY}-${pad(bM)}`
        setHistoryData(bootstrapRes)
        syncReportsUrl(selectedPvzId, bY, bM)
      } catch (err) {
        if (isCancelled) return
        console.error('[ReportsPage] Error loading daily history:', err)
        setError(err.message || 'Ошибка загрузки истории отчетов')
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    bootstrap()

    return () => {
      isCancelled = true
    }
  }, [selectedPvzId, retryTrigger])

  // 3. Навигация по месяцам (явные параметры from / to)
  const fetchMonth = async (targetYear, targetMonth) => {
    if (!selectedPvzId || !businessToday) return

    const [bY, bM] = businessToday.split('-').map(Number)
    const isTargetFuture = targetYear > bY || (targetYear === bY && targetMonth > bM)
    if (isTargetFuture) return

    const isTargetCurrent = targetYear === bY && targetMonth === bM
    const lastDay = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate()
    const from = `${targetYear}-${pad(targetMonth)}-01`
    const to = isTargetCurrent ? businessToday : `${targetYear}-${pad(targetMonth)}-${pad(lastDay)}`

    lastHandledExternalMonthRef.current = `${targetYear}-${pad(targetMonth)}`

    setLoading(true)
    setError(null)

    try {
      const res = await api.getDailyReportsHistory(selectedPvzId, from, to)
      setCurrentYear(targetYear)
      setCurrentMonth(targetMonth)
      visibleMonthRef.current = { year: targetYear, month: targetMonth }
      setHistoryData(res)
      syncReportsUrl(selectedPvzId, targetYear, targetMonth)
    } catch (err) {
      console.error('[ReportsPage] Error navigating month:', err)
      setError(err.message || 'Ошибка загрузки истории отчетов')
    } finally {
      setLoading(false)
    }
  }

  const handlePrevMonth = () => {
    if (!currentYear || !currentMonth) return
    let targetYear = currentYear
    let targetMonth = currentMonth - 1
    if (targetMonth < 1) {
      targetMonth = 12
      targetYear -= 1
    }
    fetchMonth(targetYear, targetMonth)
  }

  const handleNextMonth = () => {
    if (isFutureOrCurrent || !currentYear || !currentMonth) return
    let targetYear = currentYear
    let targetMonth = currentMonth + 1
    if (targetMonth > 12) {
      targetMonth = 1
      targetYear += 1
    }
    fetchMonth(targetYear, targetMonth)
  }

  const handleGoToCurrentMonth = () => {
    if (!todayYear || !todayMonth) return
    if (currentYear === todayYear && currentMonth === todayMonth) {
      syncReportsUrl(selectedPvzId, todayYear, todayMonth)
      return
    }
    fetchMonth(todayYear, todayMonth)
  }

  const handleRetry = () => {
    setRetryTrigger((v) => v + 1)
  }

  // Явный выбор ПВЗ в селекторе: немедленно обновляет URL до старта бутстрапа
  const handlePvzChange = useCallback((newPvzId) => {
    executePvzSelection(newPvzId, {
      selectedPvzId,
      visibleMonthRef,
      currentYear,
      currentMonth,
      syncUrl: syncReportsUrl,
      setSelectedPvzId,
      pageParams
    })
  }, [selectedPvzId, currentYear, currentMonth, pageParams])

  // Синхронизация при возврате или обновлении pageParams
  useEffect(() => {
    if (pageParams?.pvzId && pageParams.pvzId !== selectedPvzId) {
      setSelectedPvzId(pageParams.pvzId)
    }
  }, [pageParams?.pvzId, selectedPvzId])

  // Реакция на изменение внешнего параметра pageParams.month
  // ИСПРАВЛЕНИЕ ISSUE-014: слушаем строго pageParams?.month без currentYear/currentMonth,
  // предотвращая обратный откат при клике на предыдущий месяц.
  useEffect(() => {
    if (pageParams?.month && /^\d{4}-\d{2}$/.test(pageParams.month)) {
      if (pageParams.month !== lastHandledExternalMonthRef.current) {
        lastHandledExternalMonthRef.current = pageParams.month
        const [pY, pM] = pageParams.month.split('-').map(Number)
        if (currentYear !== pY || currentMonth !== pM) {
          fetchMonth(pY, pM)
        }
      }
    }
  }, [pageParams?.month])

  // 4. Пакетный расчет дневного ФОТ (только если canSeePayroll === true, канонический calculateDayPayroll)
  // REPORTS_DAY_FOT строго равен DAILY_REPORT_DAY_FOT при идентичных данных
  const dailyPayrollMap = useMemo(() => {
    if (!historyData?.canSeePayroll || !historyData?.payrollContext?.salaryConfig) {
      return {}
    }

    const {
      salaryConfig,
      salaryConfigHistory = [],
      payments = [],
      deductions = [],
      employees = [],
      schedules = [],
      workLogs = []
    } = historyData.payrollContext

    const pvzTimezone = historyData.pvz?.timezone || 'Europe/Moscow'
    const days = historyData.days || []
    const map = {}

    days.forEach((day) => {
      const res = calculateDayPayroll({
        dayDate: day.businessDate,
        employees,
        schedules,
        workLogs,
        payments,
        deductions,
        salaryConfig,
        salaryConfigHistory,
        pvzTimezone
      })
      map[day.businessDate] = res
    })

    return map
  }, [historyData])


  // Переход к детальному отчету дня с контекстом возврата
  const handleOpenDayReport = (dateStr) => {
    if (onPageChange) {
      const y = visibleMonthRef.current?.year || currentYear
      const m = visibleMonthRef.current?.month || currentMonth
      const payload = buildDailyReportNavigationPayload(selectedPvzId, dateStr, y, m)
      onPageChange('daily_report', payload)
    }
  }

  // Опции для селектора ПВЗ
  const pvzOptions = useMemo(() => {
    return userPvzs.map((p) => ({
      value: p.id,
      label: p.name || 'ПВЗ'
    }))
  }, [userPvzs])

  const selectedPvzName = useMemo(() => {
    const matched = userPvzs.find((p) => p.id === selectedPvzId)
    return matched ? matched.name : historyData?.pvz?.name || 'Пункт выдачи'
  }, [userPvzs, selectedPvzId, historyData?.pvz?.name])

  // Проверка, есть ли данные хоть в одном дне месяца
  const hasAnyDataInPeriod = useMemo(() => {
    if (!historyData?.days || historyData.days.length === 0) return false
    return historyData.days.some((d) => d.hasData)
  }, [historyData?.days])

  return (
    <div className="reports-page">
      {/* Шапка страницы */}
      <div className="reports-header">
        <div className="reports-header-title-block">
          <h1 className="reports-page-title">Отчёты</h1>
          <p className="reports-page-subtitle">Ежедневная управленческая аналитика и архив смен</p>
        </div>

        <div className="reports-controls-bar">
          {/* Селектор ПВЗ */}
          <div className="reports-pvz-control">
            {userPvzs.length > 1 ? (
              <div className="reports-select-wrapper">
                <CustomSelect
                  options={pvzOptions}
                  value={selectedPvzId}
                  onChange={handlePvzChange}
                  placeholder="Выберите ПВЗ"
                />
              </div>
            ) : (
              <div className="reports-single-pvz-badge">
                <span className="reports-single-pvz-icon">📍</span>
                <span className="reports-single-pvz-name">{selectedPvzName}</span>
              </div>
            )}
          </div>

          {/* Навигатор по месяцам */}
          <div className="reports-month-navigator">
            <button
              className="btn btn-icon btn-ghost reports-nav-arrow"
              onClick={handlePrevMonth}
              aria-label="Предыдущий месяц"
              type="button"
            >
              ◀
            </button>

            <span className="reports-month-label">{getMonthLabel(currentYear, currentMonth)}</span>

            <button
              className="btn btn-icon btn-ghost reports-nav-arrow"
              onClick={handleNextMonth}
              disabled={isFutureOrCurrent}
              aria-label={isFutureOrCurrent ? 'Будущие месяцы недоступны' : 'Следующий месяц'}
              type="button"
            >
              ▶
            </button>

            <button
              className="btn btn-sm btn-ghost reports-current-month-btn"
              onClick={handleGoToCurrentMonth}
              type="button"
            >
              Текущий месяц
            </button>
          </div>
        </div>
      </div>

      {/* Основной контент: Ежедневные отчеты */}
      <div className="reports-content-section">
        <div className="reports-section-header">
          <h2 className="reports-section-title">Ежедневные отчёты</h2>
          {historyData && (
            <span className="reports-count-label">
              {historyData.days.length} {pluralize(historyData.days.length, 'день', 'дня', 'дней')}
            </span>
          )}
        </div>

        {/* Состояние: Ошибка */}
        {error && (
          <div className="reports-error-state card">
            <div className="reports-error-icon">⚠️</div>
            <div className="reports-error-content">
              <h3>Не удалось загрузить историю</h3>
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={handleRetry} type="button">
                Повторить попытку
              </button>
            </div>
          </div>
        )}

        {/* Состояние: Загрузка */}
        {loading && !error && (
          <div className="reports-days-list">
            {[1, 2, 3, 4, 5].map((idx) => (
              <div key={idx} className="reports-day-card card reports-day-skeleton">
                <div className="reports-skeleton-line reports-skeleton-date"></div>
                <div className="reports-skeleton-line reports-skeleton-metrics"></div>
                <div className="reports-skeleton-line reports-skeleton-status"></div>
              </div>
            ))}
          </div>
        )}

        {/* Состояние: Нет доступных ПВЗ */}
        {!loading && !error && userPvzs.length === 0 && !selectedPvzId && (
          <div className="reports-empty-state card">
            <div className="reports-empty-icon">📍</div>
            <h3>Нет доступных пунктов выдачи</h3>
            <p>У вашей учетной записи нет назначенных пунктов выдачи заказов.</p>
          </div>
        )}

        {/* Состояние: Весь период пуст */}
        {!loading && !error && historyData && !hasAnyDataInPeriod && (
          <div className="reports-empty-state card">
            <div className="reports-empty-icon">📅</div>
            <h3>За {getMonthLabel(currentYear, currentMonth).toLowerCase()} нет данных</h3>
            <p>В этом месяце еще не было зарегистрированных смен и операций по данному пункту.</p>
            <button className="btn btn-secondary btn-sm" onClick={handleGoToCurrentMonth} type="button">
              Перейти к текущему месяцу
            </button>
          </div>
        )}

        {/* Состояние: Список дней */}
        {!loading && !error && historyData && hasAnyDataInPeriod && (
          <div className="reports-days-list">
            {historyData.days.map((day) => {
              const isToday = day.businessDate === todayStr
              const isYesterday = day.businessDate === yesterdayStr

              return (
                <div
                  key={day.businessDate}
                  className={`reports-day-card card ${!day.hasData ? 'reports-day-card-empty' : ''}`}
                  onClick={() => handleOpenDayReport(day.businessDate)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleOpenDayReport(day.businessDate)
                    }
                  }}
                >
                  <div className="reports-day-header">
                    <div className="reports-day-date-group">
                      <span className="reports-day-date-title">{formatDayTitle(day.businessDate)}</span>
                      {isToday && <span className="badge badge-info reports-date-badge">Сегодня</span>}
                      {isYesterday && <span className="badge badge-secondary reports-date-badge">Вчера</span>}
                    </div>

                    <div className="reports-day-status-group">
                      {day.status === 'open' && (
                        <span className="badge badge-warning reports-status-badge">
                          <span className="reports-pulse-dot"></span> Открыта смена
                        </span>
                      )}
                      {day.status === 'closed' && (
                        <span className="badge badge-success reports-status-badge">Все смены закрыты</span>
                      )}
                      {day.status === 'no_data' && (
                        <span className="reports-no-data-text">Нет зарегистрированных смен</span>
                      )}
                    </div>
                  </div>

                  {day.hasData && (
                    <div className="reports-day-metrics-row">
                      <span className="reports-metric-item">
                        <strong>{day.shiftSummary?.uniqueEmployees ?? 0}</strong>{' '}
                        {pluralize(
                          day.shiftSummary?.uniqueEmployees ?? 0,
                          'сотрудник',
                          'сотрудника',
                          'сотрудников'
                        )} ·{' '}
                        <strong>{formatHours(day.totalHours)}</strong> ч
                      </span>

                      <span className="reports-metric-divider">·</span>

                      <span className="reports-metric-item">
                        <strong>{day.operationSummary.total}</strong>{' '}
                        {pluralize(day.operationSummary.total, 'операция', 'операции', 'операций')}
                      </span>

                      {historyData.canSeePayroll && (
                        <>
                          <span className="reports-metric-divider">·</span>
                          {dailyPayrollMap[day.businessDate]?.isAvailable ? (
                            <span className="reports-metric-item reports-metric-fot">
                              ФОТ <strong>{formatMoney(dailyPayrollMap[day.businessDate].totalDailyPayroll)}</strong>
                            </span>
                          ) : (
                            <span className="reports-metric-item reports-metric-fot reports-metric-fot-unavailable">
                              ФОТ недоступен
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="reports-day-actions">
                    <button
                      className="btn btn-sm btn-ghost reports-open-day-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenDayReport(day.businessDate)
                      }}
                      type="button"
                    >
                      Открыть отчёт →
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
